import { Constants } from '../common/Constants';
import { Logger } from '../common/Logger';
import { ScopeCheck } from '../common/ScopeCheck';
import { UserState } from '../data/UserState';

export class LocalStorageHandler {
	private readonly _logger: Logger;

	constructor() {
		this._logger = new Logger(Constants.FILE_PX_API);
		const pushState = this.getValue(Constants.STOR_PUSH_STATE);
		if (!pushState) {
			this._initNewStorage();
		}
	}

	get localStorage() {
		return localStorage;
	}

	private _initNewStorage() {
		const newUserState = this.getNewUserState();
		this.saveUserState(newUserState);
		return newUserState;
	}

	getNewUserState(): UserState {
		if (ScopeCheck.isTestModeActive()) {
			return new UserState(true);
		}
		return new UserState();
	}

	getValue(key: string): string | null {
		if (this.localStorage && key) {
			return this.localStorage.getItem(key);
		}
		return null;
	}

	updateValue(key: string, newValue: string) {
		if (this.localStorage && key && newValue) {
			this.localStorage.setItem(key, newValue);
		}
	}

	readUserState(): UserState | undefined {
		const pushStateRaw = this.getValue(Constants.STOR_PUSH_STATE);
		if (!pushStateRaw) {
			return this._initNewStorage();
		}
		return this._parseExistingUserState(pushStateRaw);
	}

	saveUserState(userState: UserState) {
		if (userState) {
			try {
				const userStateString = JSON.stringify(userState.userStateJson);
				this.updateValue(Constants.STOR_PUSH_STATE, userStateString);
			} catch (error) {
				const message = `Saving user state failed. ${error.message}`;
				this._logger.error(message);
			}
		}
	}

	_parseExistingUserState(pushStateRaw: string): UserState | undefined {
		try {
			const parsedPushState = JSON.parse(pushStateRaw);
			if (parsedPushState) {
				const newUserState = this.getNewUserState();
				newUserState.initFromJsonObject(parsedPushState);
				return newUserState;
			}
		} catch (error) {
			const message = `Reading user state failed.' ${error.message}`;
			this._logger.error(message);
		}
		return undefined;
	}
}
